import React, { useState, useEffect } from 'react';
import { Handshake, X } from 'lucide-react';

const DISMISS_KEY_PREFIX = 'mif:dismiss_intro_nudge:';
const DISMISS_DAYS = 7;
const REENGAGEMENT_DAYS = 90;

const daysBetween = (isoDate) => {
  if (!isoDate) return null;
  const t = new Date(isoDate).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400_000);
};

/**
 * Shown to:
 *  - First-timers who just qualified to make a Private Introduction.
 *  - Returning users whose most recent intro is older than REENGAGEMENT_DAYS
 *    (gentle re-engagement nudge with different copy).
 * 'I'll do this later' parks the current cycle's banner for DISMISS_DAYS.
 *
 * Visual style is intentionally premium / understated — no amber alarm, no
 * SaaS-y growth copy. Matches MyInvestFolio's house style.
 */
export const IntroductionNudgeBanner = ({ eligibility, user, onGoToIntroductions }) => {
  const eligible = eligibility?.eligible === true;
  const total = eligibility?.total_introductions || 0;
  const lastIntroAt = eligibility?.last_introduction_at || null;
  const daysSinceLast = daysBetween(lastIntroAt);
  const isFirstTimer = total === 0;
  const isReengagement = total > 0 && daysSinceLast !== null && daysSinceLast >= REENGAGEMENT_DAYS;
  const shouldShow = eligible && (isFirstTimer || isReengagement);

  // Scope the dismiss key by cycle so a brand-new 90-day cycle gets its own
  // 7-day dismiss window (a stale dismissal from a previous cycle won't
  // carry over and silently swallow the re-engagement banner).
  const cycleId = isFirstTimer ? 'first' : (lastIntroAt || 'none');
  const dismissKey = user?.user_id ? `${DISMISS_KEY_PREFIX}${user.user_id}:${cycleId}` : null;
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!dismissKey) { setDismissed(true); return; }
    const dismissedAt = parseInt(localStorage.getItem(dismissKey) || '0', 10);
    if (!dismissedAt) { setDismissed(false); return; }
    const expired = Date.now() - dismissedAt > DISMISS_DAYS * 86400_000;
    if (expired) {
      localStorage.removeItem(dismissKey);
      setDismissed(false);
    } else {
      setDismissed(true);
    }
  }, [dismissKey]);

  if (!shouldShow || dismissed || !user) return null;

  const handleLater = () => {
    if (dismissKey) localStorage.setItem(dismissKey, String(Date.now()));
    setDismissed(true);
  };

  const headline = isFirstTimer
    ? 'You can now make a Private Introduction'
    : "You haven't introduced anyone in a while";
  const subline = isFirstTimer
    ? 'Introduce a trusted peer to MyInvestFolio. Up to 5 Introductions per week.'
    : 'Care to invite another investor to MyInvestFolio? Up to 5 Introductions per week.';
  const variant = isFirstTimer ? 'first-timer' : 'reengagement';

  return (
    <div
      className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="introduction-nudge-banner"
      data-variant={variant}
    >
      <div className="shrink-0 mt-0.5 bg-emerald-50 p-1.5 rounded">
        <Handshake className="w-4 h-4 text-emerald-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-900 font-medium" data-testid="introduction-nudge-headline">
          {headline}
        </p>
        <p className="text-xs text-slate-600 mt-0.5">
          {subline}
        </p>
        <button
          onClick={onGoToIntroductions}
          className="mt-2 text-xs font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
          data-testid="introduction-nudge-cta"
        >
          {isFirstTimer ? 'Introduce a peer →' : 'Introduce another investor →'}
        </button>
      </div>
      <button
        onClick={handleLater}
        className="shrink-0 text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2 mt-0.5"
        title={`Hide for ${DISMISS_DAYS} days`}
        data-testid="introduction-nudge-later"
      >
        I&apos;ll do this later
      </button>
      <button
        onClick={handleLater}
        className="shrink-0 text-slate-400 hover:text-slate-600 p-1"
        title={`Hide for ${DISMISS_DAYS} days`}
        data-testid="introduction-nudge-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default IntroductionNudgeBanner;
